import {
  Box,
  BoxProps,
  Center,
  Flex,
  Spinner,
  Text,
  TextProps
} from "@chakra-ui/react";
import { defaultScroll } from "chakra/theme";
import { filterArrayBy } from "utils/filter-array-by";
import { FocusEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { getRelativeRect } from "utils/relative-rect";
import { Checkbox } from "components/checkbox";
import { ChevronDownIcon } from "@chakra-ui/icons";

export interface InputBoxSearchProps {
  options: any[];
  checkIsInclude: (option: any) => boolean;
  onChange: (value: any) => void;
  inputBoxProps?: BoxProps;
  isLoading?: boolean;
  optionValue?: string;
  optionLabel?: string;
  optionLabelRender?: (option: any) => any;
  searchKeys?: string[];
  errorMessage?: string;
  clearInput?: boolean;
  openOnFocus?: boolean;
  clearInputOnFocus?: boolean;
  fillInputOnBlur?: boolean;
  value?: string;
  isInvalid?: boolean;
  multiSelect?: boolean;
  resultLimit?: number;
  closeOnSelect?: boolean;
  isDisabled?: boolean;
  partialSearch?: boolean;
  messageErrorProps?: TextProps;
}

export function InputBoxSearch({
  options,
  onChange: onChangeValue,
  inputBoxProps,
  isLoading,
  searchKeys = ["name"],
  optionLabel = "name",
  optionValue = "value",
  errorMessage,
  resultLimit = 50,
  checkIsInclude,
  optionLabelRender,
  clearInput = true,
  openOnFocus,
  value = "",
  isInvalid,
  multiSelect,
  closeOnSelect = true,
  isDisabled,
  partialSearch = true,
  messageErrorProps,
  clearInputOnFocus,
  fillInputOnBlur
}: InputBoxSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [result, setResult] = useState<any[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLDivElement>(null);
  const isOpened = isOpen && (openOnFocus || result.length);
  const invalidBorder = "3px solid var(--chakra-colors-secondary-600)";
  const border = "1px solid var(--chakra-colors-custom-gray)";
  const errorBox = useRef<HTMLDivElement>(null);
  const inputValueRef = useRef<string>("");

  if (errorMessage) isInvalid = true;

  const clearInputSearch = () => {
    input.current!.textContent = "";
  };

  const setInputValue = (value: string) => {
    if (input.current) input.current.textContent = value;
  };

  const handleScroll = (element: HTMLElement, parent: HTMLElement) => {
    const relRef = getRelativeRect(element, parent);
    const elHeight = element.clientHeight;
    const top = relRef.top + parent.scrollTop!;
    const scrollPos = parent.scrollTop! + parent.clientHeight;
    if (top + elHeight > scrollPos)
      parent.scrollTop = top + elHeight - parent.clientHeight;
    if (top < parent.scrollTop) parent.scrollTop = top;
  };

  let currentOptions = result.slice(0, resultLimit);

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    const list = listContainerRef.current;

    if (key === "Escape") setIsOpen(false);
    else if (result.length) setIsOpen(true);

    if (key === "Enter" && isOpened) {
      const currResult = result[selectedIndex];
      if (currResult) {
        onChangeValue(currResult[optionValue]);
        if (!multiSelect) input.current?.blur();
        setIsOpen(false);
      }
    } else if (key === "ArrowUp" && isOpened) {
      const newCurrIndex = selectedIndex - 1;
      const currResult = result?.[newCurrIndex];
      if (!currResult) {
        const selectedHtml = list!.children[
          result.length - 1
        ] as HTMLDivElement;
        handleScroll(selectedHtml, list!);
        setSelectedIndex(result.length - 1);
        if (clearInput) clearInputSearch();
      } else {
        const selectedHtml = list!.children[newCurrIndex] as HTMLDivElement;
        handleScroll(selectedHtml, list!);
        setSelectedIndex(newCurrIndex);
        if (clearInput) clearInputSearch();
      }
    } else if (key === "ArrowDown" && isOpened) {
      const newCurrIndex = selectedIndex + 1;
      const currResult = result?.[newCurrIndex];
      if (!currResult) {
        const selectedHtml = list!.children[0] as HTMLDivElement;
        handleScroll(selectedHtml, list!);
        setSelectedIndex(0);
        if (clearInput) clearInputSearch();
      } else {
        const selectedHtml = list!.children[newCurrIndex] as HTMLDivElement;
        handleScroll(selectedHtml, list!);
        setSelectedIndex(newCurrIndex);
        if (clearInput) clearInputSearch();
      }
    } else setSelectedIndex(-1);
    if (key === "Enter") e.preventDefault();
  };

  useEffect(() => {
    if (openOnFocus) setResult(options.slice(0, resultLimit));
    if (input.current) setInputValue(value);
  }, [options]);

  useEffect(() => {
    if (!isLoading) {
      if (!clearInput && input.current) setInputValue(value);
    } else {
      setInputValue("");
    }
  }, [value, isLoading]);

  useEffect(() => {
    inputValueRef.current = value;
  }, [value]);

  const {
    onChange: onChangeInputValue,
    bg,
    ...restInputProps
  } = inputBoxProps || {};

  if (options.filter((item) => checkIsInclude(item))?.length && multiSelect) {
    const selecteds = options.filter((item) => checkIsInclude(item));
    console.log(selecteds);
    const unSelecteds = currentOptions.filter((item) => !checkIsInclude(item));
    currentOptions = [...selecteds, ...unSelecteds];
  }

  const onFocus = (e: FocusEvent<HTMLDivElement>) => {
    const iconBox = e.currentTarget.parentElement
      ?.children[1] as HTMLDivElement;

    e.currentTarget.style.borderTop = border;
    e.currentTarget.style.borderLeft = border;
    e.currentTarget.style.borderBottom = border;

    iconBox.style.borderTop = border;
    iconBox.style.borderRight = border;
    iconBox.style.borderBottom = border;

    if (clearInputOnFocus) setInputValue("");
    setIsOpen(true);
    setResult(openOnFocus ? options.slice(0, resultLimit) : []);
    onFocusIn();
    inputBoxProps?.onFocus?.(e);
  };

  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    const iconBox = e.currentTarget.parentElement
      ?.children[1] as HTMLDivElement;

    e.currentTarget.style.borderTop = "";
    e.currentTarget.style.borderLeft = "";
    e.currentTarget.style.borderBottom = "";

    iconBox.style.borderTop = "";
    iconBox.style.borderRight = "";
    iconBox.style.borderBottom = "";

    if (fillInputOnBlur)
      setTimeout(() => setInputValue(inputValueRef.current), 0);
    setIsOpen(false);
    onFocusOut();
    inputBoxProps?.onBlur?.(e);
  };

  const onFocusIn = () => {
    if (errorMessage && errorBox.current) {
      errorBox.current.style.transform = `scale(0.8)`;
      errorBox.current.style.visibility = `hidden`;
      errorBox.current.style.opacity = `0`;
    }
  };
  const onFocusOut = () => {
    if (errorBox.current) {
      errorBox.current.style.transform = ``;
      errorBox.current.style.visibility = ``;
      errorBox.current.style.opacity = ``;
    }
  };

  return (
    <Box w="100%" gap="16px">
      <Box w="100%" pos="relative" role="group">
        <Flex pos="relative">
          <Box
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            overflow="hidden"
            ref={input}
            h="32px"
            w="100%"
            borderRadius="0.375rem 0 0 0.375rem"
            borderTop={isInvalid ? invalidBorder : border}
            borderLeft={isInvalid ? invalidBorder : border}
            borderBottom={isInvalid ? invalidBorder : border}
            px="16px"
            py="4px"
            bg={bg}
            cursor="pointer"
            alignItems="center"
            _focus={{ outline: "none" }}
            contentEditable={!isDisabled && !isLoading}
            onKeyDown={handleKeyPress}
            onKeyUp={(e) => {
              const pressedKey = e.key;
              const restrictedKeys = [
                "ArrowDown",
                "ArrowUp",
                "ArrowLeft",
                "ArrowRight"
              ].includes(pressedKey);

              if (!restrictedKeys) {
                const value = e.currentTarget.textContent;
                if (value) {
                  setResult(
                    filterArrayBy(options, value, searchKeys, {
                      partialSearch
                    }).slice(0, resultLimit)
                  );
                } else {
                  setResult(openOnFocus ? options.slice(0, resultLimit) : []);
                }
                onChangeInputValue?.(e);
              }
            }}
            opacity={isLoading || isDisabled ? 0.5 : undefined}
            pointerEvents={isLoading || isDisabled ? "none" : undefined}
            {...restInputProps}
            onFocus={onFocus}
            onBlur={onBlur}
          ></Box>
          {isLoading ? (
            <Center
              opacity={"0.5"}
              position="absolute"
              top="0"
              bottom="0"
              left="0"
              w="32px"
              h="32px"
            >
              <Spinner w="16px" h="16px" my="3px" />
            </Center>
          ) : null}
          <Center
            w="32px"
            h="32px"
            bg={bg}
            borderRadius="0 0.375rem 0.375rem 0"
            borderTop={isInvalid ? invalidBorder : border}
            borderRight={isInvalid ? invalidBorder : border}
            borderBottom={isInvalid ? invalidBorder : border}
            cursor="pointer"
            onClick={(e) => {
              if (input.current) input.current.focus();
            }}
            opacity={isLoading || isDisabled ? 0.5 : undefined}
            pointerEvents={isLoading || isDisabled ? "none" : undefined}
          >
            <ChevronDownIcon w="20px" h="20px" display="block" right="5px" />
          </Center>
        </Flex>
        {errorMessage ? (
          <Box
            ref={errorBox}
            bg="secondary.700"
            maxW="80%"
            minW="26px"
            pos="absolute"
            borderRadius="10px"
            p="3px"
            right={0}
            top="25px"
            zIndex="10"
            border="1px solid var(--chakra-colors-secondary-400)"
            transition="0.2s"
            transform="scale(0.8)"
            visibility="hidden"
            opacity={0}
            _groupHover={{
              transform: "scale(1)",
              visibility: "visible",
              opacity: "1"
            }}
          >
            <Box
              w="10px"
              h="10px"
              zIndex="-1"
              transform="rotate(45deg)"
              top="-6px"
              bg="secondary.700"
              right="10px"
              pos="absolute"
              borderTop="1px solid var(--chakra-colors-secondary-400)"
              borderLeft="1px solid var(--chakra-colors-secondary-400)"
              borderRadius="0 0 10px 0"
            />
            <Text
              color="#fff"
              fontWeight="bold"
              fontSize="9"
              whiteSpace="pre-wrap"
              {...messageErrorProps}
            >
              {errorMessage}&nbsp;
            </Text>
          </Box>
        ) : null}
        {isOpened ? (
          <Box
            ref={listContainerRef}
            pos="absolute"
            bottom="-2px"
            left="0"
            right="0"
            zIndex="1"
            transform="translateY(100%)"
            bg="#fff"
            borderRadius="10px"
            overflow="auto"
            border="1px solid var(--chakra-colors-gray-200)"
            maxH={"240px"}
            sx={defaultScroll}
            onMouseDown={(e) => e.preventDefault()}
          >
            {currentOptions.map((item, index) => {
              const isInclude = checkIsInclude(item);
              const isSelected = index === selectedIndex;
              return (
                <Box
                  p="5px 10px"
                  key={`input-search-${index}`}
                  w="100%"
                  _hover={{ bg: "gray.100" }}
                  bg={
                    isInclude ? "gray.200" : isSelected ? "gray.100" : undefined
                  }
                  cursor="pointer"
                  onClick={(e: any) => {
                    onChangeValue(item[optionValue]);
                    setSelectedIndex(index);
                    if (clearInput) clearInputSearch();
                    if (closeOnSelect) {
                      setIsOpen(false);
                      input.current?.blur();
                    }
                  }}
                  textAlign="start"
                  as="button"
                >
                  <Flex alignItems="center">
                    {multiSelect ? (
                      <Checkbox
                        mr="5px"
                        isChecked={isInclude}
                        onClick={(e) => {}}
                      />
                    ) : undefined}{" "}
                    {optionLabelRender?.(item) ?? `${item[optionLabel]}`}
                  </Flex>
                </Box>
              );
            })}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
